import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { ActionIcon, Flex, Switch, Tooltip } from '@mantine/core';
import { Trans, useTranslation } from 'react-i18next';
import { useFetchUserHasPermissions } from '../../../api/user/useFetchUserHasPermissions';
import Icon from '../../../components/Icon/Icon';
import { LinkComponent } from '../../../components/Link/LinkComponent';
import { ManifestBreadcrumbs } from '../../../components/ManifestBreadcrumbs/ManifestBreadcrumbs';
import { usePutOrganizationEnableInviteOnly } from '../../../hooks/mutations/usePostOrganization';
import { useFetchOrganization } from '../../../hooks/queries/useFetchOrganization';
import { useAuth } from '../../../hooks/useAuth';
import { useOrganizationId } from '../../../hooks/utils/useOrganizationId';
import representOrganization from '../../../lib/representOrganization';
import { PeopleTable } from './PeopleTable';

export const PeoplePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentOrgId] = useOrganizationId();
  const { data: organization, isLoading: isLoadingCurrentOrganization } =
    useFetchOrganization({ organizationId: currentOrgId });
  const { mutateAsync: postOrganization, isLoading: isUpdatingOrg } =
    usePutOrganizationEnableInviteOnly();
  const hasWriteAccess = useFetchUserHasPermissions({
    requiredPermissions: ['create:user', 'update:user', 'delete:user'],
  });

  const handleChangeInviteOnly = async (value: boolean) => {
    await postOrganization({
      body: { enableInviteOnly: !value },
      id: currentOrgId ?? '',
    });
  };

  const isParentOrg = !organization?.parent || organization?.parent?.length < 1;

  return (
    <div>
      {user?.organization?.parentOrg && (
        <ManifestBreadcrumbs
          breadcrumbs={[
            {
              label: user?.organization.parentOrg.name,
              tooltipText: t('page.userSettings.organization.people.breadcrumbs.parent', {
                parentOrgName: user?.organization.parentOrg.name,
              }),
              onClick: () =>
                representOrganization(user?.organization?.parentOrg?.id ?? ''),
            },
            {
              label: user?.organization?.name,
              tooltipText: t('page.userSettings.organization.people.breadcrumbs.child', {
                currentOrgName: user?.organization?.name,
              }),
              isDisabled: true,
            },
          ]}
        />
      )}
      <Flex justify="space-between" mt="16px" mb="8px">
        <TextComponent component="h2" variant="headerXLarge">
          {t('page.userSettings.organization.people.header')}
        </TextComponent>
      </Flex>
      <TextComponent variant="bodyMedium" color="muted">
        {user?.organization?.isParentOrg ? (
          <Trans
            i18nKey={'page.userSettings.organization.people.description'}
            components={{
              1: <LinkComponent href="../suborganizations" size="medium" />,
            }}
          />
        ) : (
          t('page.userSettings.organization.people.description-child')
        )}
      </TextComponent>

      {isParentOrg && (
        <Flex align="center" mt="18px">
          <Switch
            aria-label={t('page.userSettings.organization.people.invite-only-toggle')}
            label={
              <Trans
                i18nKey={`page.userSettings.organization.people.invite-only-${
                  user?.organization?.formattedDomains ? 'toggle' : 'no-domains'
                }`}
                values={{
                  domain: user?.organization?.formattedDomains,
                }}
              />
            }
            disabled={Boolean(
              isUpdatingOrg ||
                isLoadingCurrentOrganization ||
                !hasWriteAccess ||
                (user?.organization?.domains?.length ?? 0) === 0,
            )}
            style={{ marginRight: 10 }}
            checked={organization?.enableInviteOnly ? false : true}
            onChange={(e) => handleChangeInviteOnly(e.target.checked)}
          />
          <Tooltip
            multiline
            width={300}
            label={t('page.userSettings.organization.people.invite-tooltip')}
          >
            <ActionIcon color="gray.5">
              <Icon icon="circle-info" />
            </ActionIcon>
          </Tooltip>
        </Flex>
      )}

      <PeopleTable hasEditPermissions={hasWriteAccess} />
    </div>
  );
};

export default PeoplePage;
